import { useEffect, useRef, useState } from 'react';

/**
 * Offline shell UX (2026-10-29, fix/offline-shell — Jaime directive:
 * "In the case that we do not have Internet connection, it should always load
 * up the app and if there is a connection, then it should automatically update
 * or do a pull of the data").
 *
 * 1) Offline banner: a subtle fixed bar whenever the browser reports
 *    navigator.onLine === false. The app itself keeps running on cached data
 *    (service worker offline shell) — the banner just explains WHY data may be
 *    stale, and never blocks interaction.
 * 2) Auto data pull on reconnect: when connectivity returns after an offline
 *    period, bump a remount key around the routed content so every mounted
 *    page re-runs its load effects — network-first fetches then always win,
 *    matching "automatically update or do a pull of the data".
 */

// Peer-review secondary (2026-10-30): an offline period shorter than this is a
// connectivity flap, not a real outage — skip the remount so in-progress form
// state survives brief oscillations.
const OFFLINE_REMOUNT_MIN_MS = 3000;

export function ConnectivityGate({ children }: { children: React.ReactNode }) {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  const [justReconnected, setJustReconnected] = useState(false);
  const [epoch, setEpoch] = useState(0);
  // F1 fix (peer review of 0992166): initialize from the SAME condition as the
  // online state above. If the app BOOTS while already offline (the directive's
  // core scenario), the boot-time navigator.onLine === false must count as
  // "was offline"; the previous useRef(false) start made the later online event
  // skip the remount and stale/cached data survived the whole session.
  const wasOfflineRef = useRef<boolean>(
    typeof navigator === 'undefined' ? false : navigator.onLine === false,
  );
  // Secondary (b): record when the outage started so sub-second flaps do not
  // remount. null = the outage began before mount (boot-offline): duration is
  // unknown and the remount must fire (it IS the directive's core scenario).
  const offlineStartedAtRef = useRef<number | null>(null);
  // Secondary (c): keep the reconnect-banner timeout id so a new online event
  // (or unmount) clears the previous timer instead of stacking them.
  const reconnectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const goOffline = () => {
      wasOfflineRef.current = true;
      if (offlineStartedAtRef.current === null) {
        offlineStartedAtRef.current = Date.now();
      }
      setOnline(false);
    };
    const goOnline = () => {
      setOnline(true);
      const wasOffline = wasOfflineRef.current;
      wasOfflineRef.current = false;
      const startedAt = offlineStartedAtRef.current;
      offlineStartedAtRef.current = null;
      // Only remount on a real offline->online transition (the 'online' event
      // also fires on some platforms at boot; a fresh mount refetches anyway).
      // Boot-offline (startedAt === null, F1) always remounts; a measured
      // outage remounts only once it lasted >= OFFLINE_REMOUNT_MIN_MS.
      if (wasOffline && (startedAt === null || Date.now() - startedAt >= OFFLINE_REMOUNT_MIN_MS)) {
        setEpoch((e) => e + 1);
        setJustReconnected(true);
        if (reconnectTimerRef.current !== null) {
          window.clearTimeout(reconnectTimerRef.current);
        }
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          setJustReconnected(false);
        }, 4000);
      }
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {!online && (
        <div
          className="fixed inset-x-0 top-0 z-[9999] bg-amber-500 text-white text-xs sm:text-sm font-medium text-center py-1.5 px-3 shadow-md"
          role="status"
          data-testid="offline-banner"
        >
          Sin conexión — mostrando datos guardados
        </div>
      )}
      {online && justReconnected && (
        <div
          className="fixed inset-x-0 top-0 z-[9999] bg-emerald-500 text-white text-xs sm:text-sm font-medium text-center py-1.5 px-3 shadow-md"
          role="status"
          data-testid="reconnected-banner"
        >
          Conexión restablecida — actualizando datos…
        </div>
      )}
      {/* display:contents keeps layout untouched; key bump remounts routed
          pages so their fetch effects re-run (fresh pull on reconnect). */}
      <div key={epoch} style={{ display: 'contents' }}>{children}</div>
    </>
  );
}
